import { cn } from '@/lib/utils';

export type SkeletonVariant = 'chart' | 'table' | 'funnel' | 'shop' | 'kpis';

/** Heights the skeletons reserve; the live report renders at about the same size. */
export const SKELETON_HEIGHT: Record<SkeletonVariant, number> = {
  chart: 356,
  table: 390,
  funnel: 360,
  shop: 640,
  kpis: 200,
};

/**
 * The same reservations as responsive classes (YOM switches to a compact
 * layout under 480 px, so phones get shorter charts). Measured from the
 * rendered reports; the report then fills the box, so nothing jumps.
 */
export const RESERVE_CLASS: Record<SkeletonVariant, string> = {
  chart: 'min-h-[298px] sm:min-h-[356px]',
  table: 'min-h-[390px]',
  funnel: 'min-h-[360px]',
  shop: 'min-h-[700px] sm:min-h-[640px]',
  kpis: 'min-h-[264px] sm:min-h-[200px]',
};

const CHART_BARS = [38, 52, 44, 63, 58, 71, 49, 66, 80, 62, 74, 90, 68, 77];

function Bar({ className }: { className?: string }) {
  return <div className={cn('bg-muted animate-pulse rounded', className)} />;
}

/**
 * Placeholder in the shape of the report that will replace it, so nothing
 * jumps when data lands (no CLS). Matches `yom-report::part(container)`.
 */
export function ReportSkeleton({
  variant,
  title,
  className,
}: {
  variant: SkeletonVariant;
  title?: string;
  className?: string;
}) {
  return (
    <div
      role="status"
      aria-label={title ? `Loading ${title}` : 'Loading'}
      className={cn('bg-card flex h-full flex-col gap-5 rounded-xl border p-5 shadow', className)}
    >
      {title ? (
        <div className="text-base font-semibold">{title}</div>
      ) : (
        <Bar className="h-4 w-32" />
      )}
      {variant === 'chart' && (
        <div className="flex flex-1 items-end gap-1.5 pt-6">
          {CHART_BARS.map((h, i) => (
            <div
              key={i}
              className="bg-muted flex-1 animate-pulse rounded-sm"
              style={{ height: `${h}%` }}
            />
          ))}
        </div>
      )}
      {(variant === 'table' || variant === 'funnel' || variant === 'shop') && (
        <div className="flex flex-1 flex-col gap-4">
          {[92, 74, 61, 48, 35, 22]
            .slice(0, variant === 'funnel' ? 3 : variant === 'shop' ? 5 : 6)
            .map((w, i) => (
              <div key={i} className="space-y-1.5">
                <div className="flex justify-between gap-4">
                  <Bar className="h-3.5 w-1/2" />
                  <Bar className="h-3.5 w-10 shrink-0" />
                </div>
                <div className="bg-muted/60 h-1.5 overflow-hidden rounded-full">
                  <div className="bg-muted h-full animate-pulse" style={{ width: `${w}%` }} />
                </div>
              </div>
            ))}
        </div>
      )}
      {variant === 'kpis' && (
        <div className="grid flex-1 grid-cols-2 gap-3 sm:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="bg-muted/60 space-y-2 rounded-lg p-3">
              <Bar className="h-3 w-16" />
              <Bar className="h-6 w-20" />
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between">
        <Bar className="h-2.5 w-28" />
        <Bar className="h-2.5 w-16" />
      </div>
    </div>
  );
}

/** Whole-dashboard placeholder in the final layout (range bar, cards, charts). */
export function DashboardSkeleton({ cards = 4 }: { cards?: number }) {
  return (
    <div className="space-y-5 sm:space-y-6" aria-busy>
      <div className="bg-muted/60 h-9 w-full max-w-md animate-pulse rounded-lg" />
      <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        {Array.from({ length: cards }, (_, i) => (
          <div
            key={i}
            className="bg-card min-h-[148px] space-y-3 rounded-xl border p-4 shadow sm:p-5"
          >
            <Bar className="h-4 w-24" />
            <Bar className="h-8 w-20" />
            <Bar className="h-3 w-28" />
          </div>
        ))}
      </div>
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2" style={{ height: SKELETON_HEIGHT.chart }}>
          <ReportSkeleton variant="chart" />
        </div>
        <div
          className="bg-card hidden flex-col gap-3 rounded-xl border p-5 shadow lg:flex"
          style={{ height: SKELETON_HEIGHT.chart }}
        >
          <Bar className="h-4 w-24" />
          <Bar className="mt-2 h-12 w-16" />
          <Bar className="h-3 w-44" />
          <div className="mt-4 space-y-2.5">
            <Bar className="h-3 w-full" />
            <Bar className="h-3 w-4/5" />
          </div>
        </div>
      </div>
    </div>
  );
}
